import { get, set } from 'idb-keyval';

/**
 * v5 — Personnalisation du bouton "Commander" du panier
 *
 * Permet à l'admin de choisir un template OU de personnaliser entièrement :
 * couleurs (uni ou dégradé), forme, bordure, effet néon, animation, taille, etc.
 */

export type OrderBtnTemplate =
  | 'fireGourmet'   // 1 - Dégradé orange → rouge (par défaut)
  | 'neonPink'      // 2 - Néon rose flashy
  | 'goldPremium'   // 3 - Or avec reflet shine
  | 'greenPulse'    // 4 - Vert avec onde pulsante
  | 'neonCyan'      // 5 - Néon cyan transparent
  | 'blackGold'     // 6 - Noir & or premium
  | 'cosmic'        // 7 - Dégradé violet → rose
  | 'redOutline'    // 8 - Blanc avec contour rouge
  | 'custom';

export type BgMode = 'solid' | 'gradient';
export type IconType = 'cart' | 'arrow' | 'check' | 'heart' | 'flame' | 'star' | 'none';
export type IconPosition = 'left' | 'right';
export type Radius = 'none' | 'sm' | 'md' | 'lg' | 'xl' | 'pill';
export type BorderWidth = 'none' | 'thin' | 'thick';
export type GlowType = 'none' | 'shadow' | 'neon' | 'doubleRing' | 'pulse';
export type GlowIntensity = 'low' | 'medium' | 'high';
export type AnimType =
  | 'none'
  | 'pulse'
  | 'bounce'
  | 'shine'
  | 'neonFlicker'
  | 'shake';
export type AnimSpeed = 'slow' | 'medium' | 'fast';
export type BtnSize = 'sm' | 'md' | 'lg' | 'xl';

export interface OrderButtonConfig {
  enabled: boolean;
  template: OrderBtnTemplate;
  // === Texte ===
  customText: string;          // "" = utilise t('Commander') par défaut
  showPrice: boolean;          // afficher · 11.50€
  // === Icône ===
  icon: IconType;
  iconPosition: IconPosition;
  // === Couleurs ===
  bgMode: BgMode;
  bgColor1: string;            // couleur principale
  bgColor2: string;            // couleur fin de dégradé
  gradientAngle: number;       // 0-360
  textColor: string;
  // === Forme ===
  radius: Radius;
  borderWidth: BorderWidth;
  borderColor: string;
  // === Effet "autour" ===
  glow: GlowType;
  glowColor: string;           // si vide, utilise bgColor1
  glowIntensity: GlowIntensity;
  // === Animation ===
  animation: AnimType;
  animSpeed: AnimSpeed;
  // === Taille ===
  size: BtnSize;
}

const STORAGE_KEY = 'orderButtonDesign';

export const DEFAULT_ORDER_BUTTON: OrderButtonConfig = {
  enabled: false,              // OFF par défaut → bouton actuel intact
  template: 'fireGourmet',
  customText: '',
  showPrice: false,
  icon: 'cart',
  iconPosition: 'left',
  bgMode: 'gradient',
  bgColor1: '#f97316',
  bgColor2: '#dc2626',
  gradientAngle: 135,
  textColor: '#ffffff',
  radius: 'lg',
  borderWidth: 'none',
  borderColor: '#f97316',
  glow: 'shadow',
  glowColor: '#f97316',
  glowIntensity: 'medium',
  animation: 'none',
  animSpeed: 'medium',
  size: 'md',
};

/* ============================================
 * 8 Templates pré-faits
 * ============================================ */
export const ORDER_BTN_TEMPLATES: Record<
  Exclude<OrderBtnTemplate, 'custom'>,
  { label: string; emoji: string; description: string; config: Partial<OrderButtonConfig> }
> = {
  fireGourmet: {
    label: 'Feu Gourmet',
    emoji: '🔥',
    description: 'Dégradé orange feu',
    config: {
      template: 'fireGourmet',
      bgMode: 'gradient',
      bgColor1: '#f97316',
      bgColor2: '#dc2626',
      gradientAngle: 135,
      textColor: '#ffffff',
      radius: 'lg',
      borderWidth: 'none',
      glow: 'shadow',
      glowColor: '#f97316',
      glowIntensity: 'medium',
      animation: 'none',
      icon: 'cart',
    },
  },
  neonPink: {
    label: 'Néon Rose',
    emoji: '⚡',
    description: 'Halo lumineux rose',
    config: {
      template: 'neonPink',
      bgMode: 'solid',
      bgColor1: '#ec4899',
      bgColor2: '#ec4899',
      textColor: '#ffffff',
      radius: 'lg',
      borderWidth: 'none',
      glow: 'neon',
      glowColor: '#ec4899',
      glowIntensity: 'high',
      animation: 'neonFlicker',
      animSpeed: 'medium',
      icon: 'cart',
    },
  },
  goldPremium: {
    label: 'Or Premium',
    emoji: '💎',
    description: 'Reflet doré qui passe',
    config: {
      template: 'goldPremium',
      bgMode: 'gradient',
      bgColor1: '#fbbf24',
      bgColor2: '#f59e0b',
      gradientAngle: 90,
      textColor: '#1f2937',
      radius: 'xl',
      borderWidth: 'none',
      glow: 'shadow',
      glowColor: '#f59e0b',
      glowIntensity: 'medium',
      animation: 'shine',
      animSpeed: 'medium',
      icon: 'cart',
    },
  },
  greenPulse: {
    label: 'Vert Pulse',
    emoji: '🌿',
    description: 'Onde pulsante verte',
    config: {
      template: 'greenPulse',
      bgMode: 'solid',
      bgColor1: '#10b981',
      bgColor2: '#10b981',
      textColor: '#ffffff',
      radius: 'pill',
      borderWidth: 'none',
      glow: 'pulse',
      glowColor: '#10b981',
      glowIntensity: 'medium',
      animation: 'pulse',
      animSpeed: 'medium',
      icon: 'check',
    },
  },
  neonCyan: {
    label: 'Néon Cyan',
    emoji: '🌊',
    description: 'Transparent cyan futuriste',
    config: {
      template: 'neonCyan',
      bgMode: 'solid',
      bgColor1: 'transparent',
      bgColor2: 'transparent',
      textColor: '#06b6d4',
      radius: 'lg',
      borderWidth: 'thick',
      borderColor: '#06b6d4',
      glow: 'neon',
      glowColor: '#06b6d4',
      glowIntensity: 'high',
      animation: 'pulse',
      animSpeed: 'medium',
      icon: 'arrow',
    },
  },
  blackGold: {
    label: 'Black Gold',
    emoji: '🌙',
    description: 'Noir et or, premium',
    config: {
      template: 'blackGold',
      bgMode: 'solid',
      bgColor1: '#1f2937',
      bgColor2: '#1f2937',
      textColor: '#fbbf24',
      radius: 'lg',
      borderWidth: 'thick',
      borderColor: '#fbbf24',
      glow: 'shadow',
      glowColor: '#000000',
      glowIntensity: 'medium',
      animation: 'none',
      icon: 'star',
    },
  },
  cosmic: {
    label: 'Cosmique',
    emoji: '🦄',
    description: 'Dégradé violet → rose',
    config: {
      template: 'cosmic',
      bgMode: 'gradient',
      bgColor1: '#8b5cf6',
      bgColor2: '#ec4899',
      gradientAngle: 135,
      textColor: '#ffffff',
      radius: 'lg',
      borderWidth: 'none',
      glow: 'shadow',
      glowColor: '#8b5cf6',
      glowIntensity: 'high',
      animation: 'bounce',
      animSpeed: 'medium',
      icon: 'heart',
    },
  },
  redOutline: {
    label: 'Contour Rouge',
    emoji: '🎯',
    description: 'Blanc + double contour',
    config: {
      template: 'redOutline',
      bgMode: 'solid',
      bgColor1: '#ffffff',
      bgColor2: '#ffffff',
      textColor: '#dc2626',
      radius: 'lg',
      borderWidth: 'thick',
      borderColor: '#dc2626',
      glow: 'doubleRing',
      glowColor: '#dc2626',
      glowIntensity: 'medium',
      animation: 'none',
      icon: 'flame',
    },
  },
};

export function applyOrderBtnTemplate(
  current: OrderButtonConfig,
  template: Exclude<OrderBtnTemplate, 'custom'>
): OrderButtonConfig {
  return {
    ...current,
    ...ORDER_BTN_TEMPLATES[template].config,
    template,
  };
}

export async function getOrderButtonDesign(): Promise<OrderButtonConfig> {
  try {
    const stored = await get<OrderButtonConfig>(STORAGE_KEY);
    if (stored && typeof stored === 'object') {
      return { ...DEFAULT_ORDER_BUTTON, ...stored };
    }
  } catch (e) {
    console.error('[OrderButtonDesign] load error', e);
  }
  return DEFAULT_ORDER_BUTTON;
}

export async function saveOrderButtonDesign(
  cfg: OrderButtonConfig
): Promise<boolean> {
  try {
    await set(STORAGE_KEY, cfg);
    window.dispatchEvent(
      new CustomEvent('orderButtonDesignUpdated', { detail: cfg })
    );
    return true;
  } catch (e) {
    console.error('[OrderButtonDesign] save error', e);
    return false;
  }
}

/* ============================================
 * Helpers pour générer les styles CSS
 * ============================================ */

const RADIUS_PX: Record<Radius, string> = {
  none: '0',
  sm: '6px',
  md: '8px',
  lg: '12px',
  xl: '16px',
  pill: '9999px',
};

const BORDER_PX: Record<BorderWidth, string> = {
  none: '0',
  thin: '1px',
  thick: '2.5px',
};

const SIZE_STYLES: Record<BtnSize, { padding: string; fontSize: string }> = {
  sm: { padding: '8px 12px', fontSize: '13px' },
  md: { padding: '12px 18px', fontSize: '15px' },
  lg: { padding: '16px 24px', fontSize: '17px' },
  xl: { padding: '20px 30px', fontSize: '19px' },
};

const INTENSITY_MULT: Record<GlowIntensity, number> = {
  low: 0.5,
  medium: 1,
  high: 1.5,
};

const ANIM_DURATION: Record<AnimSpeed, string> = {
  slow: '2.5s',
  medium: '1.6s',
  fast: '0.9s',
};

const ANIM_CLASS: Record<AnimType, string> = {
  none: '',
  pulse: 'order-btn-anim-pulse',
  bounce: 'order-btn-anim-bounce',
  shine: 'order-btn-anim-shine',
  neonFlicker: 'order-btn-anim-neon',
  shake: 'order-btn-anim-shake',
};

/**
 * Génère le style CSS final du bouton à partir de la config.
 */
export function getOrderButtonStyles(cfg: OrderButtonConfig): {
  style: React.CSSProperties;
  className: string;
} {
  const size = SIZE_STYLES[cfg.size];
  const radius = RADIUS_PX[cfg.radius];
  const borderW = BORDER_PX[cfg.borderWidth];
  const intensity = INTENSITY_MULT[cfg.glowIntensity];
  const animDur = ANIM_DURATION[cfg.animSpeed];
  const animCls = ANIM_CLASS[cfg.animation];

  // Background
  let background: string;
  if (cfg.bgMode === 'gradient') {
    background = `linear-gradient(${cfg.gradientAngle}deg, ${cfg.bgColor1}, ${cfg.bgColor2})`;
  } else {
    background = cfg.bgColor1;
  }

  // Glow / shadow
  const glowCol = cfg.glowColor || cfg.bgColor1;
  let boxShadow = 'none';
  switch (cfg.glow) {
    case 'shadow':
      boxShadow = `0 ${4 * intensity}px ${14 * intensity}px ${glowCol}66`;
      break;
    case 'neon':
      boxShadow = `0 0 ${8 * intensity}px ${glowCol}, 0 0 ${20 * intensity}px ${glowCol}, 0 0 ${30 * intensity}px ${glowCol}66`;
      break;
    case 'doubleRing':
      boxShadow = `0 0 0 ${3 * intensity}px ${glowCol}30, 0 0 0 ${6 * intensity}px ${glowCol}15`;
      break;
    case 'pulse':
      // L'animation pulse sera gérée par la classe CSS séparément
      boxShadow = `0 ${4 * intensity}px ${14 * intensity}px ${glowCol}66`;
      break;
    case 'none':
    default:
      boxShadow = 'none';
  }

  // Style de base
  const style: React.CSSProperties = {
    background,
    color: cfg.textColor,
    padding: size.padding,
    fontSize: size.fontSize,
    borderRadius: radius,
    border: cfg.borderWidth === 'none' ? 'none' : `${borderW} solid ${cfg.borderColor}`,
    boxShadow,
    fontWeight: 700,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    width: '100%',
    transition: 'transform 0.15s ease, filter 0.15s ease',
    // Custom CSS variables for animations
    ['--order-btn-glow' as any]: glowCol,
    ['--order-btn-anim-dur' as any]: animDur,
    ['--order-btn-intensity' as any]: String(intensity),
  };

  return {
    style,
    className: `order-btn-custom ${animCls}`.trim(),
  };
}
